/** G2「交易与账」契约解析与取数（campaign #4591 · W2 #4601）。
 *
 * 形状**以后端为准**（主人 2026-10-08 裁决）：`someo_keel/api/console_g2.py` 的五个账目端点
 * 共用信封 `run_id` / `strategy_id` / `mode` / `trade_date`；`/orders` 直接给 `avg_px`、`commissions`
 * （裁决 B10，前端不推导）；`/risk/overrides` 的 `active` 由服务端判定（前端不比时间）。
 *
 * 不做默认值填充：后端承诺必有的字段缺了就抛 `ContractError`；后端约定可为 `null` 的字段
 * 原样保留 `null`，由屏显示「缺」。
 */

import { useQuery } from "@tanstack/react-query";

import {
  apiUrl,
  expectList,
  expectRecord,
  optionalNumber,
  optionalString,
  query,
  requireBoolean,
  requireNumber,
  requireString,
  type QueryValue,
} from "./client";
import { ContractError, isRetryable } from "./errors";
import type { ReadResult } from "./queries";

// ---- 类型 ------------------------------------------------------------------

/** 一本账的信封：说明这是哪一次运行的账。当前账未绑定运行时四项都是 `null`。 */
export interface LedgerEnvelope {
  run_id: string | null;
  strategy_id: string | null;
  mode: string | null;
  trade_date: string | null;
}

/** 选账参数：都不给 = 当前账。 */
export interface BookParams {
  run_id?: string | null;
  strategy?: string | null;
}

export interface G2Order {
  client_order_id: string;
  venue_order_id: string | null;
  instrument_id: string;
  side: string | null;
  quantity: number | null;
  status: string;
  ts_event: number;
  reason: string | null;
  filled_qty: number;
  /** 含滑点成交价按量加权；没有成交为 `null`。 */
  avg_px: number | null;
  /** 该单成交手续费逐笔加总（成本项）。 */
  commissions: number;
}

export interface G2Trade {
  trade_id: string;
  client_order_id: string;
  instrument_id: string;
  side: string;
  last_qty: number;
  last_px: number;
  slipped_px: number;
  commission: number;
  ts_event: number;
  position_id: string | null;
  /** 服务端显式带出的 `@property`；缺失为 `null`（屏显示「缺」，不按 0 计）。 */
  slippage_cost: number | null;
}

export interface G2Position {
  position_id: string;
  instrument_id: string;
  side: string;
  quantity: number;
  avg_px_open: number;
  avg_px_close: number | null;
  /** 内核仓位事件原值（仓位口径，只作对照）。 */
  realized_pnl: number;
  ts_event: number;
  closed: boolean;
  mark_px: number | null;
  mark_trade_date: string | null;
  market_value: number | null;
  unrealized_pnl: number | null;
}

export interface G2Decision {
  client_order_id: string;
  signal_id: string;
  instrument_id: string;
  direction: string;
  target_weight: number;
  research_flow: string;
  params_digest: string;
  data_snapshot: string;
  ts_event: number;
  /** 只有风控压缩了数量才出现。 */
  risk_adjustment: Record<string, unknown> | null;
  /** 待加字段：后端未给时为 `null`，屏显示「缺」。 */
  research_version: string | null;
}

export interface G2Disposition {
  signal_id: string;
  instrument_id: string;
  disposition: string;
  ts_event: number;
  order_id: string | null;
  detail: string | null;
}

export interface Book<T> extends LedgerEnvelope {
  rows: T;
}

export interface OverrideRecord {
  scope: string;
  operator: string;
  reason: string;
  granted_utc: string;
  expires_utc: string;
  /** 服务端按当前时间判定；未上线的旧后端没有此字段时为 `null`（屏显示「缺」）。 */
  active: boolean | null;
}

/** `GET /risk/limits` 里本组用到的部分：各层上限与放行有效期上限。 */
export interface RiskLimitsRaw {
  digest: string;
  limits: Record<string, Record<string, unknown>>;
  max_ttl_hours: number;
}

/** `GET /runs/{run_id}` 里「凭什么 / 版本」用到的部分。 */
export interface RunTrace {
  run_id: string;
  config_digest: string | null;
  snapshot_id: string | null;
  ledger_digest: string | null;
  accepted: string[] | null;
  commissions: number | null;
  slippage_cost: number | null;
  total_pnl: number | null;
  realized_pnl: number | null;
  cash_change: number | null;
  /** `versions` 对象（待加）；旧运行没有时为 `null`。 */
  versions: Record<string, unknown> | null;
}

export interface ReconResult {
  checked: boolean;
  balanced: boolean | null;
  missing_locally: string[];
  missing_on_channel: string[];
  mismatched: string[];
  summary: string | null;
}

/** 账户口径 KPI：日报（纸上盘）→ 权益序列 → 运行归档（回测）三个出处之一。 */
export interface AccountBasis {
  source: string;
  basis: string | null;
  total_pnl: number | null;
  realized_closed: number | null;
  unrealized: number | null;
  cash_change: number | null;
  /** 旧产物只有现金变动、没有权益口径时为 true。 */
  legacy: boolean;
}

// ---- 解析 ------------------------------------------------------------------

function envelope(source: Record<string, unknown>, url: string): LedgerEnvelope {
  for (const key of ["run_id", "strategy_id", "mode", "trade_date"]) {
    if (!(key in source)) throw new ContractError(`账目信封缺少字段 \`${key}\``, url, source);
  }
  return {
    run_id: optionalString(source, "run_id"),
    strategy_id: optionalString(source, "strategy_id"),
    mode: optionalString(source, "mode"),
    trade_date: optionalString(source, "trade_date"),
  };
}

function nullableNumber(source: Record<string, unknown>, key: string, url: string): number | null {
  const value = source[key];
  if (value === null) return null;
  return requireNumber(source, key, url);
}

export function parseOrdersBook(body: unknown): Book<G2Order[]> {
  const url = apiUrl("/orders");
  const source = expectRecord(body, url, "订单响应");
  const rows = expectList(source, "orders", url, "订单").map((row) => ({
    client_order_id: requireString(row, "client_order_id", url),
    venue_order_id: optionalString(row, "venue_order_id"),
    instrument_id: optionalString(row, "instrument_id") ?? "",
    side: optionalString(row, "side"),
    quantity: optionalNumber(row, "quantity"),
    status: requireString(row, "status", url),
    ts_event: requireNumber(row, "ts_event", url),
    reason: optionalString(row, "reason"),
    filled_qty: optionalNumber(row, "filled_qty") ?? 0,
    avg_px: nullableNumber(row, "avg_px", url),
    commissions: requireNumber(row, "commissions", url),
  }));
  return { ...envelope(source, url), rows };
}

export function parseFillsBook(body: unknown): Book<G2Trade[]> {
  const url = apiUrl("/fills");
  const source = expectRecord(body, url, "成交响应");
  const rows = expectList(source, "trades", url, "成交").map((row) => ({
    trade_id: requireString(row, "trade_id", url),
    client_order_id: requireString(row, "client_order_id", url),
    instrument_id: optionalString(row, "instrument_id") ?? "",
    side: requireString(row, "side", url),
    last_qty: requireNumber(row, "last_qty", url),
    last_px: requireNumber(row, "last_px", url),
    slipped_px: requireNumber(row, "slipped_px", url),
    commission: requireNumber(row, "commission", url),
    ts_event: requireNumber(row, "ts_event", url),
    position_id: optionalString(row, "position_id"),
    slippage_cost: optionalNumber(row, "slippage_cost"),
  }));
  return { ...envelope(source, url), rows };
}

export function parsePositionsBook(body: unknown): Book<G2Position[]> {
  const url = apiUrl("/positions");
  const source = expectRecord(body, url, "持仓响应");
  const rows = expectList(source, "positions", url, "持仓").map((row) => ({
    position_id: requireString(row, "position_id", url),
    instrument_id: optionalString(row, "instrument_id") ?? "",
    side: requireString(row, "side", url),
    quantity: requireNumber(row, "quantity", url),
    avg_px_open: requireNumber(row, "avg_px_open", url),
    avg_px_close: optionalNumber(row, "avg_px_close"),
    realized_pnl: requireNumber(row, "realized_pnl", url),
    ts_event: requireNumber(row, "ts_event", url),
    closed: row["closed"] === true,
    mark_px: optionalNumber(row, "mark_px"),
    mark_trade_date: optionalString(row, "mark_trade_date"),
    market_value: optionalNumber(row, "market_value"),
    unrealized_pnl: optionalNumber(row, "unrealized_pnl"),
  }));
  return { ...envelope(source, url), rows };
}

export function parseDecisionsBook(body: unknown): Book<Record<string, G2Decision>> {
  const url = apiUrl("/decisions");
  const source = expectRecord(body, url, "决策留痕响应");
  const map = expectRecord(source["decisions"], url, "decisions");
  const rows: Record<string, G2Decision> = {};
  for (const [id, value] of Object.entries(map)) {
    const row = expectRecord(value, url, `decisions.${id}`);
    const adjustment = row["risk_adjustment"];
    rows[id] = {
      client_order_id: optionalString(row, "client_order_id") ?? id,
      signal_id: requireString(row, "signal_id", url),
      instrument_id: optionalString(row, "instrument_id") ?? "",
      direction: optionalString(row, "direction") ?? "",
      target_weight: optionalNumber(row, "target_weight") ?? Number.NaN,
      research_flow: optionalString(row, "research_flow") ?? "",
      params_digest: optionalString(row, "params_digest") ?? "",
      data_snapshot: optionalString(row, "data_snapshot") ?? "",
      ts_event: requireNumber(row, "ts_event", url),
      risk_adjustment:
        adjustment && typeof adjustment === "object" && !Array.isArray(adjustment)
          ? (adjustment as Record<string, unknown>)
          : null,
      research_version: optionalString(row, "research_version"),
    };
  }
  return { ...envelope(source, url), rows };
}

export function parseDispositionsBook(body: unknown): Book<G2Disposition[]> {
  const url = apiUrl("/dispositions");
  const source = expectRecord(body, url, "信号下场响应");
  const rows = expectList(source, "dispositions", url, "信号下场").map((row) => ({
    signal_id: requireString(row, "signal_id", url),
    instrument_id: optionalString(row, "instrument_id") ?? "",
    disposition: requireString(row, "disposition", url),
    ts_event: requireNumber(row, "ts_event", url),
    order_id: optionalString(row, "order_id"),
    detail: optionalString(row, "detail"),
  }));
  return { ...envelope(source, url), rows };
}

export function parseOverrides(body: unknown): OverrideRecord[] {
  const url = apiUrl("/risk/overrides");
  return expectList(body, "overrides", url, "放行记录").map((row) => ({
    scope: requireString(row, "scope", url),
    operator: requireString(row, "operator", url),
    reason: requireString(row, "reason", url),
    granted_utc: requireString(row, "granted_utc", url),
    expires_utc: requireString(row, "expires_utc", url),
    active: typeof row["active"] === "boolean" ? requireBoolean(row, "active", url) : null,
  }));
}

export function parseRiskLimitsRaw(body: unknown): RiskLimitsRaw {
  const url = apiUrl("/risk/limits");
  const source = expectRecord(body, url, "风险限额响应");
  const limits = expectRecord(source["limits"], url, "limits");
  const override = expectRecord(limits["override"], url, "limits.override");
  const layers: Record<string, Record<string, unknown>> = {};
  for (const layer of ["instrument", "strategy", "portfolio"]) {
    layers[layer] = expectRecord(limits[layer], url, `limits.${layer}`);
  }
  return {
    digest: requireString(source, "digest", url),
    limits: layers,
    max_ttl_hours: requireNumber(override, "max_ttl_hours", url),
  };
}

export function parseRunTrace(body: unknown, runId: string): RunTrace {
  const url = apiUrl(`/runs/${encodeURIComponent(runId)}`);
  const source = expectRecord(body, url, "运行详情");
  const signals = source["signals"];
  let accepted: string[] | null = null;
  if (signals && typeof signals === "object" && !Array.isArray(signals)) {
    const list = (signals as Record<string, unknown>)["accepted"];
    if (Array.isArray(list)) accepted = list.map((item) => (typeof item === "string" ? item : String((item as { signal_id?: unknown })?.signal_id ?? item)));
  }
  const versions = source["versions"];
  return {
    run_id: requireString(source, "run_id", url),
    config_digest: optionalString(source, "config_digest"),
    snapshot_id: optionalString(source, "snapshot_id"),
    ledger_digest: optionalString(source, "ledger_digest"),
    accepted,
    commissions: optionalNumber(source, "commissions"),
    slippage_cost: optionalNumber(source, "slippage_cost"),
    total_pnl: optionalNumber(source, "total_pnl"),
    realized_pnl: optionalNumber(source, "realized_pnl"),
    cash_change: optionalNumber(source, "cash_change"),
    versions: versions && typeof versions === "object" && !Array.isArray(versions) ? (versions as Record<string, unknown>) : null,
  };
}

function stringIds(value: unknown): string[] {
  return Array.isArray(value) ? value.map((item) => String(item)) : [];
}

/** `GET /reconciliations/{run_id}`：没有对账字段时判「未对账」，不判「平」。 */
export function parseRecon(body: unknown, runId: string): ReconResult {
  const url = apiUrl(`/reconciliations/${encodeURIComponent(runId)}`);
  const source = expectRecord(body, url, "对账结果");
  if (typeof source["balanced"] !== "boolean") {
    return { checked: false, balanced: null, missing_locally: [], missing_on_channel: [], mismatched: [], summary: null };
  }
  return {
    checked: true,
    balanced: source["balanced"] as boolean,
    missing_locally: stringIds(source["missing_locally"]),
    missing_on_channel: stringIds(source["missing_on_channel"]),
    mismatched: stringIds(source["mismatched"]),
    summary: optionalString(source, "summary"),
  };
}

/** 日报 `strategies.<sid>.pnl` → 账户口径；该策略不在日报里返回 `null`。 */
export function basisFromDailyReport(body: unknown, strategy: string, date: string): AccountBasis | null {
  const url = apiUrl(`/reports/daily/${encodeURIComponent(date)}`);
  const source = expectRecord(body, url, "日报");
  const strategies = expectRecord(source["strategies"], url, "日报 strategies");
  const entry = strategies[strategy];
  if (!entry || typeof entry !== "object") return null;
  const pnl = expectRecord((entry as Record<string, unknown>)["pnl"], url, `strategies.${strategy}.pnl`);
  return {
    source: `GET /reports/daily/${date}`,
    basis: optionalString(pnl, "basis"),
    total_pnl: optionalNumber(pnl, "total_pnl"),
    realized_closed: optionalNumber(pnl, "realized_closed"),
    unrealized: optionalNumber(pnl, "unrealized"),
    cash_change: optionalNumber(pnl, "cash_change"),
    legacy: optionalNumber(pnl, "total_pnl") === null,
  };
}

/** 权益序列最后一行 → 账户口径；旧行没有 `total_pnl` 时只给现金变动并标 legacy。 */
export function basisFromEquity(body: unknown, strategy: string): AccountBasis | null {
  const url = apiUrl("/paper/equity");
  const rows = Array.isArray(body) ? body : expectList(body, "points", url, "权益序列");
  if (rows.length === 0) return null;
  const last = expectRecord(rows[rows.length - 1], url, "权益序列末行");
  const total = optionalNumber(last, "total_pnl");
  return {
    source: `GET /paper/equity?strategy=${strategy}（${optionalString(last, "trade_date") ?? "日期缺"}）`,
    basis: total === null ? null : "权益口径",
    total_pnl: total,
    realized_closed: optionalNumber(last, "realized_pnl_closed"),
    unrealized: null,
    cash_change: optionalNumber(last, "cash_change") ?? optionalNumber(last, "realized_pnl_cash"),
    legacy: total === null,
  };
}

/** 回测运行归档 → 账户口径。 */
export function basisFromRun(run: RunTrace): AccountBasis {
  return {
    source: `GET /runs/${run.run_id}`,
    basis: run.total_pnl === null ? null : "权益口径",
    total_pnl: run.total_pnl,
    realized_closed: run.realized_pnl,
    unrealized: null,
    cash_change: run.cash_change,
    legacy: run.total_pnl === null,
  };
}

// ---- 取数 ------------------------------------------------------------------

function bookQuery(params: BookParams, extra?: Record<string, QueryValue>): Record<string, QueryValue> {
  return { run_id: params.run_id ?? undefined, strategy: params.strategy ?? undefined, ...extra };
}

export const readOrdersBook = (params: BookParams = {}, status?: string) =>
  query<unknown>("/orders", bookQuery(params, { status })).then(parseOrdersBook);
export const readFillsBook = (params: BookParams = {}) => query<unknown>("/fills", bookQuery(params)).then(parseFillsBook);
export const readPositionsBook = (params: BookParams = {}) =>
  query<unknown>("/positions", bookQuery(params)).then(parsePositionsBook);
export const readDecisionsBook = (params: BookParams = {}) =>
  query<unknown>("/decisions", bookQuery(params)).then(parseDecisionsBook);
export const readDispositionsBook = (params: BookParams = {}) =>
  query<unknown>("/dispositions", bookQuery(params)).then(parseDispositionsBook);
export const readOverrides = () => query<unknown>("/risk/overrides").then(parseOverrides);
export const readRiskLimitsRaw = () => query<unknown>("/risk/limits").then(parseRiskLimitsRaw);
export const readRunTrace = (runId: string) =>
  query<unknown>(`/runs/${encodeURIComponent(runId)}`).then((body) => parseRunTrace(body, runId));
export const readRecon = (runId: string) =>
  query<unknown>(`/reconciliations/${encodeURIComponent(runId)}`).then((body) => parseRecon(body, runId));
export const readRiskReportRaw = (strategy: string, date: string) =>
  query<unknown>("/risk/report", { strategy, date }).then((body) => expectRecord(body, apiUrl("/risk/report"), "风险报告"));

const READ = {
  retry: (count: number, error: unknown) => count < 2 && isRetryable(error),
  retryDelay: 1_500,
} as const;

function useRead<T>(key: readonly unknown[], fn: () => Promise<T>, enabled = true): ReadResult<T> {
  return useQuery<T, Error>({ queryKey: key, queryFn: fn, enabled, ...READ });
}

const bookKey = (params: BookParams) => [params.run_id ?? null, params.strategy ?? null] as const;

export const useOrdersBook = (params: BookParams = {}) => useRead(["g2", "orders", ...bookKey(params)], () => readOrdersBook(params));
export const useFillsBook = (params: BookParams = {}) => useRead(["g2", "fills", ...bookKey(params)], () => readFillsBook(params));
export const usePositionsBook = (params: BookParams = {}) =>
  useRead(["g2", "positions", ...bookKey(params)], () => readPositionsBook(params));
export const useDecisionsBook = (params: BookParams = {}) =>
  useRead(["g2", "decisions", ...bookKey(params)], () => readDecisionsBook(params));
export const useDispositionsBook = (params: BookParams = {}) =>
  useRead(["g2", "dispositions", ...bookKey(params)], () => readDispositionsBook(params));
export const useOverrides = () => useRead(["g2", "overrides"], readOverrides);
export const useRiskLimitsRaw = () => useRead(["g2", "risk-limits-raw"], readRiskLimitsRaw);
export const useRunTrace = (runId: string | null) =>
  useRead(["g2", "run", runId], () => readRunTrace(runId as string), Boolean(runId));
export const useRecon = (runId: string | null) => useRead(["g2", "recon", runId], () => readRecon(runId as string), Boolean(runId));
export const useRiskReportRaw = (strategy: string, date: string) =>
  useRead(["g2", "risk-report", strategy, date], () => readRiskReportRaw(strategy, date), Boolean(strategy && date));
export const useDailyReportBasis = (strategy: string | null, date: string | null) =>
  useRead(
    ["g2", "daily-basis", strategy, date],
    () => query<unknown>(`/reports/daily/${encodeURIComponent(date as string)}`).then((body) => basisFromDailyReport(body, strategy as string, date as string)),
    Boolean(strategy && date),
  );
export const useEquityBasis = (strategy: string | null) =>
  useRead(["g2", "equity-basis", strategy], () => query<unknown>("/paper/equity", { strategy: strategy as string }).then((body) => basisFromEquity(body, strategy as string)), Boolean(strategy));

/** 账本选择器的选项：运行索引里本组只用这五个字段（不复用 `parseRuns`——它要求的回测字段纸上盘不一定有）。 */
export interface BookOption {
  run_id: string;
  mode: string | null;
  strategy_id: string | null;
  trade_date: string | null;
  ts_ns: number | null;
}

export function parseBookOptions(body: unknown): BookOption[] {
  const url = apiUrl("/runs");
  return expectList(body, "runs", url, "运行索引").map((row) => ({
    run_id: requireString(row, "run_id", url),
    mode: optionalString(row, "mode"),
    strategy_id: optionalString(row, "strategy_id"),
    trade_date: optionalString(row, "trade_date"),
    ts_ns: optionalNumber(row, "ts_ns"),
  }));
}

export const useBookOptions = () => useRead(["g2", "book-options"], () => query<unknown>("/runs").then(parseBookOptions));
